import ConfirmDialog from '@components/Common/ConfirmDialog';
import DetailSection from '@components/Common/DetailSection';
import EmptyState from '@components/Common/EmptyState';
import FilterRow from '@components/Common/FilterRow';
import RecordLink from '@components/Common/RecordLink';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { referralDrawEntriesQuery, referralDrawsQuery, runReferralDrawMutation } from '@constants/graphQLQueries';
import { formatDate } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { fmtMoney } from '@helpers/money';
import CasinoOutlinedIcon from '@mui/icons-material/CasinoOutlined';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { monthLabel } from './review';

export interface DrawEntry {
  referrerId: string;
  referrerName: string | null;
  entries: number;
}

export interface Draw {
  id: string;
  month: string;
  totalEntries: number;
  seed: string;
  pickHash: string;
  pickIndex: number;
  winnerId: string;
  winnerName: string | null;
  prize: number;
  currency: string;
  runBy: string | null;
  runAt: string | null;
  entries: DrawEntry[];
}

/** The last six finished months, newest first, as yyyy-MM in UTC: the only months that can be drawn. */
export const finishedMonths = (now: Date = new Date()): string[] => {
  const out: string[] = [];
  for (let i = 1; i <= 6; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
};

const DRAW_COLUMNS = ['month', 'entries', 'winner', 'prize', 'check'];

/**
 * Super Admin: the monthly referral draw (programme v3).
 *
 * A referrer gets one entry for each friend whose profile reached the completeness bar in the month.
 * A month can be drawn once, after it ends; the backend refuses a second run and returns the first.
 * The winner's prize goes into the review queue like any other reward, so nothing is paid from
 * here. The seed is shown so the pick can be checked: SHA-256 of seed and entries, modulo the total.
 */
const Draws: React.FC = () => {
  const { showToast } = useToast();
  const months = finishedMonths();
  const [month, setMonth] = useState(months[0]);
  const [entries, setEntries] = useState<DrawEntry[] | null>(null);
  const [draws, setDraws] = useState<Draw[] | null>(null);
  const [error, setError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadDraws = useCallback(() => {
    setError(false);
    fetchGraphQLApiData(referralDrawsQuery())
      .then((r) => setDraws(r?.data?.referralDraws ?? []))
      .catch(() => setError(true));
  }, []);

  useEffect(loadDraws, [loadDraws]);

  useEffect(() => {
    setEntries(null);
    fetchGraphQLApiData(referralDrawEntriesQuery(month))
      .then((r) => setEntries(r?.data?.referralDrawEntries ?? []))
      .catch(() => setEntries([]));
  }, [month]);

  const already = draws?.find((d) => d.month === month) ?? null;
  const total = (entries ?? []).reduce((s, e) => s + (e.entries || 0), 0);

  const run = async () => {
    setBusy(true);
    try {
      const r = await fetchGraphQLApiData(runReferralDrawMutation(month));
      const d: Draw | undefined = r?.data?.runReferralDraw;
      showToast(d ? `${monthLabel(month)} drawn: ${d.winnerName || 'a referrer'} won` : 'Drawn', 'success');
      setConfirming(false);
      loadDraws();
    } catch (e: any) {
      showToast(String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || 'The draw did not run.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DetailSection
      title="Monthly draw"
      icon={<CasinoOutlinedIcon />}
      sx={{ mb: 0 }}
      headerAction={<Chip size="small" label={`${total} ${total === 1 ? 'entry' : 'entries'} in ${monthLabel(month)}`} />}
    >
      <FilterRow>
        <SelectFormField
          required={false}
          name="drawMonth"
          label="Month"
          value={month}
          options={months.map((m) => ({ value: m, label: monthLabel(m) }))}
          onChange={(_n, v) => setMonth(String(v))}
        />
        <Button
          variant="contained"
          disabled={!!already || total === 0 || entries === null}
          onClick={() => setConfirming(true)}
        >
          {already ? 'Already drawn' : 'Run draw'}
        </Button>
      </FilterRow>

      {entries === null ? (
        <SkeletonLoader rows={2} />
      ) : entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nobody entered in {monthLabel(month)}. An entry is a referred friend who completed their profile that month.
        </Typography>
      ) : (
        <Box className="rf-chips" data-testid="rf-draw-entries">
          {entries.map((e) => (
            <Chip
              key={e.referrerId}
              size="small"
              label={`${e.referrerName || e.referrerId}: ${e.entries} ${e.entries === 1 ? 'entry' : 'entries'}`}
            />
          ))}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" component="div" className="rf-draw-history-title">
        Past draws
      </Typography>
      {error ? (
        <EmptyState compact title="Past draws could not be loaded" body="Try again in a moment." />
      ) : draws === null ? (
        <SkeletonLoader rows={3} columns={DRAW_COLUMNS.length} />
      ) : draws.length === 0 ? (
        <EmptyState compact title="No draws yet" body="A month can be drawn once it has ended." />
      ) : (
        <MintlyTable
          pagination={{ enabled: false, page: 0, rowsPerPage: draws.length, totalNumberOfRows: draws.length, handleChangePage: () => {} }}
          rows={draws.map((d) => ({ ...d, id: d.id }))}
          selectors={DRAW_COLUMNS}
          tableHeadRow={{ month: <>Month</>, entries: <>Entries</>, winner: <>Winner</>, prize: <>Prize</>, check: <>Seed</> }}
          tableBodyRow={{
            month: (d: Draw) => <>{monthLabel(d.month)}</>,
            entries: (d: Draw) => <>{`${d.totalEntries} from ${d.entries?.length ?? 0} referrers`}</>,
            winner: (d: Draw) => (
              <>
                <RecordLink to={`/jobseekers/${d.winnerId}`}>{d.winnerName || d.winnerId}</RecordLink>
                <Typography component="span" variant="body2" color="text.secondary" className="rf-payout-line">
                  {d.runAt ? `Drawn ${formatDate(d.runAt)}` : ''}
                </Typography>
              </>
            ),
            prize: (d: Draw) => <>{fmtMoney(d.prize, d.currency)}</>,
            check: (d: Draw) => (
              <Tooltip title={`Seed ${d.seed}. Pick ${d.pickIndex} of ${d.totalEntries}.`}>
                <span className="rf-seed">{d.seed?.slice(0, 12)}</span>
              </Tooltip>
            ),
          }}
        />
      )}

      <ConfirmDialog
        open={confirming}
        title={`Run the ${monthLabel(month)} draw?`}
        body={`One winner is picked at random from ${total} ${total === 1 ? 'entry' : 'entries'}. This can be done once for the month. Their prize goes to the review queue before it can be paid.`}
        actionLabel="Run draw"
        actionColor="primary"
        busy={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={() => run()}
      />
    </DetailSection>
  );
};

export default Draws;
